import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Grid2,
  FormControl,
  Stepper,
  Step,
  StepButton,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  CircularProgress,
  DialogActions,
} from '@mui/material';
import { X } from 'lucide-react';
import {
  CheckBoxes,
  DateQuestion,
  Questionnaire,
  QuestionnaireQuestion,
  RadioQuestion,
  TextQuestion,
} from '../../../types/questionnaire.types';
import { StudentProfile } from '../../../types/auth.types';
import { Project } from '../../../types/project.types';
import { questionnaireService } from '../../../services/questionnaire.service';
import { PDFCheckBox, PDFDocument, PDFRadioGroup, PDFTextField } from 'pdf-lib';
import {
  FormAnswers,
  FormSubmission,
  ProjectContext,
} from '../../../types/forms.types';
import { useAuth } from '../../../contexts/AuthContext';
import { formsService } from '../../../services/forms.service';

interface FormFillerProps {
  open: boolean;
  selectedQuestionnaire: Questionnaire;
  forms: FormSubmission[];
  user: StudentProfile | null;
  project: Project | null;
  onClose: () => void;
}

const FormFiller: React.FC<FormFillerProps> = ({
  open,
  selectedQuestionnaire,
  forms,
  user,
  project,
  onClose,
}) => {
  const defaultQuestionnaire = useCallback(() => {
    return {
      id: '-1',
      title: 'you should not be able to see this',
      description: '',
      status: 'Draft',
      regionID: '',
      fairID: '',
      questions: [],
      pdfURL: '',
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Questionnaire;
  }, []);

  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(
    defaultQuestionnaire()
  );
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});

  const [PDFBlob, setPDFBlob] = useState<Blob | null>(null);
  const [pdfBlobURL, setpdfBlobURL] = useState<string>(
    'https://firebasestorage.googleapis.com/v0/b/fairtechweb-dd440.appspot.com/o/questionnaires%2Fassets%2Fdefault.pdf?alt=media&token=bea8c8e7-8b5f-47ec-a53f-63874f4f5ded'
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const { authStatus } = useAuth();

  const loadPDF = useCallback(async (url: string) => {
    if (url !== '') {
      const blob = await questionnaireService.retrievePDF(url);
      if (blob instanceof Blob) {
        setPDFBlob(blob);
        var blobURl = URL.createObjectURL(blob);
        setpdfBlobURL(blobURl);
      }
    }
  }, []);

  // Parses string for keywords and replaces them with user and project data
  const parseForAutoFill = useCallback(
    (defaultString: string) => {
      if (user && project) {
        const dict: Record<string, string> = {
          //Student Info
          './name': user.firstName + ' ' + user.lastName,
          './phone': user.phone,
          './email': user.email,
          './address': user.address,
          './city': user.city,
          './state': user.state,
          './zipCode': user.zipCode,
          './school': user.school,
          './grade': String(user.grade),

          //Project Info
          './projectName': project.name,
          './adultSponsorName': project.adultSponsor.name,
          './adultSponsorEmail': project.adultSponsor.email,
          './projectCreatorName':
            project.members.filter((obj) => obj.role === 'creator')[0]
              .firstName +
            ' ' +
            project.members.filter((obj) => obj.role === 'creator')[0].lastName,
          './projectCreatorEmail': project.members.filter(
            (obj) => obj.role === 'creator'
          )[0].email,
        };

        var newString = defaultString;
        for (const key in dict) {
          newString = newString.replace(key, dict[key]);
        }
        return newString;
      } else {
        return defaultString;
      }
    },
    [project, user]
  );

  const editAnswer = useCallback(
    (pdfMap: string, answer: string) => {
      var newAnswers = { ...answers };
      newAnswers[pdfMap] = answer;
      setAnswers(newAnswers);
    },
    [answers]
  );

  // Parses the questionnaire and loads previously saved answers if they exist / loads default values
  const loadAnswers = useCallback(
    async (newQuestionnaire: Questionnaire) => {
      var newAnswers: Record<string, string> = {};

      var formAnswers: Record<string, string> = {};
      if (project) {
        var existingFormAnswers = await formsService.getFormAnswers(
          project.id,
          questionnaire.id
        );
        if (existingFormAnswers && existingFormAnswers.answers) {
          formAnswers = existingFormAnswers.answers;
        }
      }

      for (let i = 0; i < newQuestionnaire.questions.length; i++) {
        const question = newQuestionnaire.questions[i];
        switch (question.type) {
          case 'text':
            (question.questionData as TextQuestion).answers.forEach(
              (answer) => {
                if (formAnswers[answer.pdfMap]) {
                  newAnswers[answer.pdfMap] = formAnswers[answer.pdfMap];
                } else {
                  newAnswers[answer.pdfMap] = parseForAutoFill(
                    answer.defaultValue
                  );
                }
              }
            );
            break;
          case 'radiogroup':
            if (
              formAnswers[
                (question.questionData as RadioQuestion).pdfRadioGroup
              ]
            ) {
              newAnswers[
                (question.questionData as RadioQuestion).pdfRadioGroup
              ] =
                formAnswers[
                  (question.questionData as RadioQuestion).pdfRadioGroup
                ];
            } else {
              newAnswers[
                (question.questionData as RadioQuestion).pdfRadioGroup
              ] = String(
                (question.questionData as RadioQuestion).answers[
                  (question.questionData as RadioQuestion).defaultValue
                ].pdfRadioMap
              );
            }
            break;
          case 'checkboxes':
            (question.questionData as CheckBoxes).answers.forEach((answer) => {
              if (formAnswers[answer.pdfMap]) {
                newAnswers[answer.pdfMap] = formAnswers[answer.pdfMap];
              } else {
                newAnswers[answer.pdfMap] = String(answer.defaultValue);
              }
            });

            break;
          case 'date':
            if (formAnswers[(question.questionData as DateQuestion).pdfMap]) {
              newAnswers[(question.questionData as DateQuestion).pdfMap] =
                formAnswers[(question.questionData as DateQuestion).pdfMap];
            } else {
              newAnswers[(question.questionData as DateQuestion).pdfMap] =
                String((question.questionData as DateQuestion).startDate);
            }
            break;
          default:
            break;
        }
      }
      setAnswers(newAnswers);
    },
    [parseForAutoFill, project, questionnaire.id]
  );

  const loadQuestionnaire = useCallback(async () => {
    setLoading(true);
    setQuestionnaire(selectedQuestionnaire);
    await loadPDF(selectedQuestionnaire.pdfURL);
    loadAnswers(selectedQuestionnaire);
    setLoading(false);
  }, [selectedQuestionnaire, loadPDF, loadAnswers]);

  const reset = useCallback(() => {
    setAnswers({});
    setError(null);
    setActiveStep(0);
    setCompleted({});
    setPDFBlob(null);
    setpdfBlobURL(
      'https://firebasestorage.googleapis.com/v0/b/fairtechweb-dd440.appspot.com/o/questionnaires%2Fassets%2Fdefault.pdf?alt=media&token=bea8c8e7-8b5f-47ec-a53f-63874f4f5ded'
    );
    setLoading(false);
  }, []);

  // Load questionnaire and answers when the dialog opens
  useEffect(() => {
    if (open) {
      loadQuestionnaire();
    } else {
      reset();
    }
  }, [open, loadQuestionnaire, reset]);

  // Fill the PDF with answers
  const fillPDF = async () => {
    if (PDFBlob) {
      var pdfDoc = PDFDocument.load(await PDFBlob.arrayBuffer());
      var form = (await pdfDoc).getForm();
      const fields = form.getFields();
      for (const key in answers) {
        const field = fields.find((obj) => obj.getName() === key);
        if (field instanceof PDFTextField) {
          //Checks if the string is a date and reformats it
          if (
            answers[key][4] === '-' &&
            answers[key][7] === '-' &&
            answers[key].length === 10 &&
            Date.parse(answers[key])
          ) {
            field.setText(new Date(answers[key]).toLocaleDateString());
          } else {
            field.setText(answers[key]);
          }
        } else if (field instanceof PDFRadioGroup) {
          field.select(answers[key]);
        } else if (field instanceof PDFCheckBox) {
          if (answers[key] === 'true') {
            field.check();
          } else {
            field.uncheck();
          }
        }
      }

      const pdfBytes = await (await pdfDoc).save();
      const newBlob = new Blob([await pdfBytes], { type: 'application/pdf' });
      setPDFBlob(newBlob);
      setpdfBlobURL(URL.createObjectURL(newBlob));
    }
  };

  // Submits the filled PDF and saves answers to the database
  const handleUpload = async () => {
    const userId = authStatus.user?.uid;

    if (!PDFBlob || !userId || !user || !project) return;

    try {
      setUploading(true);
      setError(null);

      const pdfFile = new File(
        [PDFBlob],
        `${project.name} - ${user.firstName} ${user.lastName}.pdf`,
        {
          type: 'application/pdf',
        }
      );

      // Verify the there is no empty key in the answers due to firebase not allowing empty values
      var newAnswers = { ...answers };
      if (newAnswers['']) {
        delete newAnswers[''];
      }

      var existingForm = forms.find((obj) => obj.formType === questionnaire.id);
      var formAnswers = {
        projectId: project.id,
        formType: questionnaire.id,
        lastUpdated: new Date(),
        answers: newAnswers,
      } as FormAnswers;

      if (existingForm) {
        await formsService.uploadNewVersion(
          existingForm.id,
          pdfFile,
          formAnswers,
          userId
        );
      } else {
        const projectContext: ProjectContext = {
          projectId: project.id,
          projectName: project.name,
          responsibleStudentId: userId,
          responsibleStudentName: `${user.firstName} ${user.lastName}`,
          assignedAt: new Date(),
        };

        await formsService.submitProjectForm(
          projectContext,
          pdfFile,
          formAnswers,
          questionnaire.id,
          false
        );
      }
      handleSave();
    } catch (err) {
      setError('Failed to upload form. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      fillPDF();
    }
  };

  const handleSave = async () => {
    const userId = authStatus.user?.uid;

    if (!PDFBlob || !userId || !user || !project) return;

    // Verify the there is no empty key in the answers due to firebase not allowing empty values

    var newAnswers = { ...answers };
    if (newAnswers['']) {
      delete newAnswers[''];
    }

    setSaving(true);
    var existingAnswers = await formsService.getFormAnswers(
      project.id,
      questionnaire.id
    );

    var formAnswers = {
      projectId: project.id,
      formType: questionnaire.id,
      lastUpdated: new Date(),
      answers: newAnswers,
    } as FormAnswers;

    if (existingAnswers) {
      formsService.updateFormAnswers(project.id, questionnaire.id, formAnswers);
    } else {
      formsService.createFormAnswers(project.id, questionnaire.id, formAnswers);
    }
    setSaving(false);
  };

  // Render the content based on the question type
  const renderContent = (questionIndex: number) => {
    try {
      const question = questionnaire.questions[questionIndex];
      switch (question.type) {
        case 'text':
          return (
            <TextComponent
              question={question}
              answers={answers}
              editAnswer={editAnswer}
            />
          );
        case 'radiogroup':
          return (
            <RadioComponent
              question={question}
              answers={answers}
              editAnswer={editAnswer}
            />
          );
        case 'checkboxes':
          return (
            <CheckComponent
              question={question}
              answers={answers}
              editAnswer={editAnswer}
            />
          );
        case 'date':
          return (
            <DateComponent
              question={question}
              answers={answers}
              editAnswer={editAnswer}
            />
          );
        default:
          return null;
      }
    } catch {}
  };

  // Get the steps to be displayed in the stepper based on the current step
  const getViewableSteps = (middle: number) => {
    var left = middle - 2;
    var length = 5;
    if (length > questionnaire.questions.length - 1) {
      length = questionnaire.questions.length - 1;
    }
    if (left < 1) {
      left = 1;
    } else if (length + left >= questionnaire.questions.length) {
      left = questionnaire.questions.length - length;
    }
    return Array.from({ length }, (_, index) => left + index);
  };

  const totalSteps = () => {
    return questionnaire.questions.length;
  };

  const handleNext = () => {
    if (activeStep === totalSteps() - 1) {
      fillPDF();
    }
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleViewSubmission = () => {
    fillPDF();
    setActiveStep(totalSteps());
  };

  if (open && (loading || !PDFBlob)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '88vh' },
      }}
    >
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Typography component={'div'} variant="h5">
            {questionnaire.title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="space-y-6">
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            className="mb-4"
          >
            {error}
          </Alert>
        )}

        <Box sx={{ width: '100%' }}>
          <Box>
            <Grid2
              display="flex"
              justifyContent="center"
              size="grow"
              flexDirection="row"
            >
              <Stepper nonLinear style={{ width: '80%' }} alternativeLabel>
                <Step key={0} active={0 === activeStep} index={0}>
                  <StepButton color="inherit" onClick={handleStep(0)}>
                    Question 1
                  </StepButton>
                </Step>
                {getViewableSteps(activeStep).map((step) => (
                  <Step
                    key={step}
                    completed={completed[step]}
                    active={step === activeStep}
                    index={step}
                  >
                    <StepButton color="inherit" onClick={handleStep(step)}>
                      Question {step + 1}
                    </StepButton>
                  </Step>
                ))}
                <Step
                  key={totalSteps()}
                  active={totalSteps() === activeStep}
                  index={totalSteps()}
                >
                  <StepButton color="inherit" onClick={handleViewSubmission}>
                    View Submission
                  </StepButton>
                </Step>
              </Stepper>
            </Grid2>

            <div>
              {activeStep === totalSteps() ? (
                <React.Fragment>
                  <Box sx={{ height: '57vh' }}>
                    <iframe
                      width="100%"
                      height="100%"
                      src={pdfBlobURL}
                      title={'Form PDF'}
                    />
                  </Box>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Typography component={'div'} sx={{ mt: 2, mb: 1, py: 1 }}>
                    {renderContent(activeStep)}
                  </Typography>
                </React.Fragment>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                <Button
                  color="inherit"
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {activeStep === totalSteps() && (
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Submit Form'}
                  </Button>
                )}

                <Box sx={{ flex: '1 1 auto' }} />
                <Button
                  onClick={handleNext}
                  sx={{ mr: 1 }}
                  variant="contained"
                  disabled={activeStep === totalSteps()}
                >
                  Next
                </Button>
              </Box>
            </div>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface QuestionProps {
  question: QuestionnaireQuestion;
  answers: { [key: string]: string };
  editAnswer: (pdfMap: string, answer: string) => void;
}

const TextComponent: React.FC<QuestionProps> = ({
  question,
  answers,
  editAnswer,
}) => {
  try {
    return (
      <Box>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <Typography
            component={'div'}
            variant="h5"
            gutterBottom
            textAlign={'center'}
          >
            {question.questionText}
          </Typography>
        </Grid2>
        {(question.questionData as TextQuestion).answers.map(
          (answer, index) => (
            <Grid2
              display="flex"
              justifyContent="center"
              alignItems="center"
              size="grow"
              key={`${index} TextField`}
              p={1}
            >
              <TextField
                fullWidth={true}
                id="outlined-controlled"
                label={answer.label}
                multiline={true}
                value={answers[answer.pdfMap]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  editAnswer(answer.pdfMap, event.target.value)
                }
              />
            </Grid2>
          )
        )}
      </Box>
    );
  } catch {
    return null;
  }
};

const RadioComponent: React.FC<QuestionProps> = ({
  question,
  answers,
  editAnswer,
}) => {
  try {
    return (
      <Box>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <Typography
            component={'div'}
            variant="h5"
            gutterBottom
            textAlign={'center'}
            p={1}
          >
            {question.questionText}
          </Typography>
        </Grid2>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              name="radio-buttons-group"
              value={
                answers[(question.questionData as RadioQuestion).pdfRadioGroup]
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                editAnswer(
                  (question.questionData as RadioQuestion).pdfRadioGroup,
                  String(event.target.value)
                );
              }}
            >
              {(question.questionData as RadioQuestion).answers.map(
                (option, index) => (
                  <FormControlLabel
                    key={`${index} RadioButton`}
                    value={option.pdfRadioMap}
                    control={<Radio />}
                    label={option.text}
                  />
                )
              )}
            </RadioGroup>
          </FormControl>
        </Grid2>
      </Box>
    );
  } catch {
    return null;
  }
};

const CheckComponent: React.FC<QuestionProps> = ({
  question,
  answers,
  editAnswer,
}) => {
  const editCheckBox = (pdfMap: string) => {
    editAnswer(pdfMap, String(!stringToBoolean(answers[pdfMap])));
  };

  const stringToBoolean = (bool: string) => {
    return bool === 'true';
  };
  try {
    return (
      <Box>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <Typography
            component={'div'}
            variant="h5"
            gutterBottom
            textAlign={'center'}
            p={1}
          >
            {question.questionText}
          </Typography>
        </Grid2>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <FormControl>
            {(question.questionData as CheckBoxes).answers.map(
              (answer, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={stringToBoolean(answers[answer.pdfMap])}
                      onChange={() => editCheckBox(answer.pdfMap)}
                    />
                  }
                  label={answer.text}
                />
              )
            )}
          </FormControl>
        </Grid2>
      </Box>
    );
  } catch {
    return null;
  }
};

const DateComponent: React.FC<QuestionProps> = ({
  question,
  answers,
  editAnswer,
}) => {
  const [invalid, setInvalid] = useState(false);
  const [errorText, setErrorText] = useState('');

  const handleDateChange = (newDate: string) => {
    const start: Date = new Date(
      (question.questionData as DateQuestion).startDate
    );
    const date: Date = new Date(newDate);
    const end: Date = new Date((question.questionData as DateQuestion).endDate);

    if (date < start) {
      setInvalid(true);
      setErrorText(
        `Outside date range: ${date.toLocaleDateString()} is earlier than ${start.toLocaleDateString()}`
      );
    } else if (date > end) {
      setInvalid(true);
      setErrorText(
        `Outside date range:  ${date.toLocaleDateString()} is later than ${end.toLocaleDateString()}`
      );
    } else {
      setInvalid(false);
      setErrorText(`${date.toLocaleDateString()} is inside the date range`);
      editAnswer((question.questionData as DateQuestion).pdfMap, newDate);
    }
  };

  try {
    return (
      <Box>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
        >
          <Typography
            component={'div'}
            variant="h5"
            gutterBottom
            textAlign={'center'}
            p={1}
          >
            {question.questionText}
          </Typography>
        </Grid2>
        <Grid2
          display="flex"
          justifyContent="center"
          alignItems="center"
          size="grow"
          p={1}
        >
          <FormControl>
            <TextField
              id="outlined-controlled"
              label="Date"
              type="date"
              error={invalid}
              helperText={errorText}
              value={answers[(question.questionData as DateQuestion).pdfMap]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDateChange(event.target.value);
              }}
            ></TextField>
          </FormControl>
        </Grid2>
      </Box>
    );
  } catch {
    return null;
  }
};

export default FormFiller;
